import { createFileRoute, Navigate, useLocation } from "@tanstack/react-router";
import { AppStage } from "forest/sandbox/AppStage";

import { useSession } from "../auth-client";
import { usePlayground } from "../automerge/use-playground";
import { useProjectDoc } from "../automerge/use-project-doc";
import { useRepoMaybe } from "../automerge/use-repo-maybe";
import { useIdentity } from "../identity";
import { SandboxProvider } from "../sandbox/SandboxProvider";

/**
 * `/embed/<app path>` — a single App rendered **full-bleed with no Hearth
 * chrome**, on the real host (the same `SandboxProvider` + `AppHostRegistry` the
 * workspace uses): real Playground over the Automerge sync, real capabilities
 * (`/api/proxy`), real `watch`, real handshake. The App's page-runtime is
 * unchanged — it talks to a genuine parent here, just one with no UI around it.
 *
 * A minimal embed/preview surface, useful on its own; it's also the target the
 * `advanced-app-debugging` flow drives in a browser. The platform logs that
 * browser in server-side (it passes the tenant session cookie to the headless
 * browser through the MCP connection — see `HearthDO`'s `#browserAuthHeaders`),
 * so the agent just navigates here directly.
 */
export const Route = createFileRoute("/embed/$")({
  component: EmbedRoute,
});

function EmbedRoute() {
  // `_splat` is the App's directory **path** (the launcher identity — a splat
  // so its `/`s survive as path segments rather than a single param).
  const appPath = Route.useParams()._splat ?? "";
  const { data: session, isPending } = useSession();
  // Return here after signing in rather than dropping to the workspace home
  // (this deep link is a standalone preview surface). `href` is the relative
  // path + search + hash the sign-in flow accepts as `next`.
  const attemptedHref = useLocation().href;
  const identity = useIdentity();
  // EmbedHost's hooks call `useRepo()`, which throws while the wasm-gated
  // repo is still null (automerge/repo.ts).
  const repo = useRepoMaybe();

  if (isPending || (session && identity.isPending)) {
    return <Centered>Loading…</Centered>;
  }
  if (!session)
    return <Navigate to="/sign-in" search={{ next: attemptedHref }} />;
  if (!identity.data) return <Centered>Could not load workspace.</Centered>;
  if (!repo) return <Centered>Loading…</Centered>;

  return (
    <EmbedHost appPath={appPath} localBuiltins={identity.data.localBuiltins} />
  );
}

function EmbedHost({
  appPath,
  localBuiltins,
}: {
  appPath: string;
  localBuiltins: boolean;
}) {
  const { url: projectUrl, doc, isLoading, error } = useProjectDoc();
  // Gate playground construction on the project doc being ready (same reason as
  // the workspace shell: `createPlayground`'s `repo.find(root)` rejects while a
  // freshly-created doc is still syncing in).
  const { playground } = usePlayground(
    doc ? projectUrl : undefined,
    localBuiltins,
  );

  if (isLoading || !playground) return <Centered>Loading…</Centered>;
  if (error || !doc) return <Centered>Failed to load workspace.</Centered>;

  return (
    <SandboxProvider playground={playground} localBuiltins={localBuiltins}>
      <div className="h-dvh w-screen overflow-hidden bg-background">
        <AppStage appPath={appPath} className="h-full w-full" />
      </div>
    </SandboxProvider>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-dvh items-center justify-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}
